import '@/styles/studio.css';
import {Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger} from '@tryghost/shade';
import {Link, NavLink, Outlet} from 'react-router-dom';
import {LucideIcon} from '@tryghost/shade/utils';
import {TribeTaleWordmark} from '@/components/TribeTaleWordmark';
import {useAuth} from '@/contexts/AuthContext';
import {useEffect, useState} from 'react';
import {useSites} from '@/hooks/useSites';
import {useTheme} from '@/contexts/ThemeContext';

export function AppLayout() {
    useEffect(() => {
        document.body.classList.add('studio-active');
        return () => document.body.classList.remove('studio-active');
    }, []);
    const {user, logout} = useAuth();
    const {data: sites} = useSites();
    const {theme, toggleTheme} = useTheme();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const isOwner = sites?.some(site => site.access_type === 'owner') ?? false;
    const initials = (user?.name || user?.email || 'Account').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
    const navigationItems = [
        {to: '/dashboard', label: 'Newsletters'},
        ...(isOwner ? [
            {to: '/themes', label: 'Themes'},
            {to: '/team', label: 'Team'},
            {to: '/billing', label: 'Billing'},
            {to: '/settings/api-tokens', label: 'API & MCP'}
        ] : []),
        ...(user?.role === 'superadmin' ? [{to: '/superadmin', label: 'Admin'}] : [])
    ];

    return (
        <div className="shade-control-panel studio-shell">
            <a className="studio-skip-link" href="#main-content">Skip to content</a>
            <header className="studio-header">
                <div className="studio-header-inner">
                    <Link aria-label="TribeTale home" className="studio-brand" to="/dashboard"><TribeTaleWordmark /></Link>
                    <nav aria-label="Main navigation" className="studio-nav">
                        {navigationItems.map(item => <NavLink key={item.to} className="studio-nav-link" to={item.to}>{item.label}</NavLink>)}
                    </nav>
                    <div className="studio-header-actions">
                        <Button aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} className="studio-appearance-toggle" title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} type="button" variant="ghost" onClick={toggleTheme}>
                            {theme === 'dark' ? <LucideIcon.Sun size={19} strokeWidth={1.6} /> : <LucideIcon.Moon size={19} strokeWidth={1.6} />}
                        </Button>
                        <span aria-hidden="true" className="studio-header-divider" />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button aria-label="Account menu" className="studio-account-trigger" type="button" variant="ghost">
                                    <span className="studio-avatar">{initials}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="studio-account-menu" sideOffset={12}>
                                <DropdownMenuLabel className="studio-account-name">{user?.name || user?.email}</DropdownMenuLabel>
                                <DropdownMenuSeparator className="studio-menu-divider" />
                                <DropdownMenuItem className="studio-menu-item" asChild><Link to="/settings/delete-account">Delete account</Link></DropdownMenuItem>
                                <DropdownMenuItem className="studio-menu-item" onSelect={logout}>Sign Out</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <DropdownMenu modal={false} open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                            <DropdownMenuTrigger asChild>
                                <Button aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'} className="studio-mobile-nav-trigger" type="button" variant="ghost">
                                    {isMobileMenuOpen ? <LucideIcon.X size={20} strokeWidth={1.8} /> : <LucideIcon.Menu size={20} strokeWidth={1.8} />}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" aria-label="Main navigation" className="studio-account-menu studio-mobile-nav-menu" sideOffset={17} onEscapeKeyDown={() => setIsMobileMenuOpen(false)}>
                                {navigationItems.map(item => <DropdownMenuItem key={item.to} className="studio-mobile-nav-link" asChild>
                                    <NavLink to={item.to}>{item.label}</NavLink>
                                </DropdownMenuItem>)}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>
            <Outlet />
        </div>
    );
}
